import angular from "angular";

import {
  Language,
  Translations,
  Invoice,
  InvoiceType,
  InvoiceStatus
} from "@salaxy/core";
import { SessionService } from "../../services";
import {
  InvoiceMapper,
  ReportData,
  ReportOptions,
  Templates,
  MappedInvoice
} from "@salaxy/reports";


/**
 * Renders a invoice report for the given invoice.
 * Uses Handlebars templates (@salaxy/reports).
 */
export class InvoiceReportController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "Templates",
    "$sce",
    "$timeout",
    "SessionService",
  ];

  /** Invoice for the report */
  public invoice: Invoice;
  /** If true applies table-html classes and uses only invoice table */
  public applySiteStyles: boolean;

  /** Boolean indicating if the refresh is in progress. */
  public refreshInprogress: boolean;

  /**Selected report language */
  public lang: Language;

  private renderedInvoice: Invoice;

  private renderedLang: string;

  private renderedApplySiteStyles: boolean;

  private htmlData: any;

  private reportOptions: ReportOptions;

  private reportData: ReportData<MappedInvoice> = {
    headerFooter: {},
    layout: {},
    report: {},
  };

  /**
   * Creates a new InvoiceReportController
   * @ignore
   */
  constructor(
    private templates: Templates,
    private $sce: angular.ISCEService,
    private $timeout: angular.ITimeoutService,
    private sessionService: SessionService
  ) {
  }

  /**
   * Implement IController
   */
  public $onInit() {
    // no initializations
  }

  /** Bindable and trusted html. */
  public get html(): any {
    if (!this.invoice) {
      this.renderedInvoice = null;
      this.renderedLang = null;
      this.renderedApplySiteStyles = null;
    } else {
      const invoice = angular.copy(this.invoice);

      if (!this.renderedInvoice ||
        !angular.equals(invoice, this.renderedInvoice) ||
        this.renderedLang !== this.lang ||
        this.applySiteStyles !== this.renderedApplySiteStyles) {
        this.renderedApplySiteStyles = this.applySiteStyles;
        this.renderedInvoice = angular.copy(invoice);
        this.renderedLang = this.lang;
        this.getDefaultLanguage(this.lang).then((lang) => {
          this.lang = lang;
          this.renderedLang = this.lang;
          this.refresh();
        });
      }
    }
    return this.htmlData;
  }

  /** Checks if invoice is preview */
  public isPreviewInvoice(): boolean {
    return (
      [InvoiceType.Pension, InvoiceType.Unemployment].includes(
        this.invoice.header.type
      ) &&
      [InvoiceStatus.Preview, InvoiceStatus.Forecast].includes(
        this.invoice.header.status
      )
    );
  }

  /**
   * Refresh invoice report data.
   */
  private async refresh() {
    if (this.refreshInprogress) {
      return;
    }
    this.refreshInprogress = true;
    const renderingLang = this.lang ?? Language.Fi;
    await Translations.loadLanguage(renderingLang);
    this.reportData.report = await InvoiceMapper.getInvoice(this.invoice, this.getDocumentAndSvgNode);
    if(this.reportData.report.barcode) {
      this.reportData.report.barcode = new XMLSerializer().serializeToString(this.reportData.report.barcode as any);
    }
    this.reportOptions = InvoiceMapper.loadReportOptions(angular.copy(this.invoice));
    this.reportData.headerFooter = InvoiceMapper.getHeaderFooter(this.invoice, this.reportOptions, renderingLang);
    this.reportData.layout = InvoiceMapper.loadReportLayout(false);
    const css = "";
    this.reportData.layout.hasCustomCss = false;
    this.reportData.layout.customCss = (css || "");
    this.reportData.layout.margin = {
      top: this.reportOptions.margin.top,
      right: this.reportOptions.margin.right,
      bottom: this.reportOptions.margin.bottom,
      left: this.reportOptions.margin.left
    };
    const templates = new Templates();
    this.reportData.headerHtml = this.reportOptions?.headerTemplate ? templates.getHtmlDynamic(this.reportOptions.headerTemplate, this.reportData.headerFooter, renderingLang) : "";
    this.reportData.footerHtml = this.reportOptions?.headerTemplate ? templates.getHtmlDynamic(this.reportOptions.footerTemplate, this.reportData.headerFooter, renderingLang) : "";
    this.htmlData = this.$sce.trustAsHtml(this.templates.getHtml(this.applySiteStyles ? "invoiceTable" : "invoice", this.reportData, renderingLang));
    this.refreshInprogress = false;
    this.$timeout();
  }

  private getDocumentAndSvgNode() {
    return {document, svgNode: document.createElementNS("http://www.w3.org/2000/svg", "svg")};
  }

  private async getDefaultLanguage(lang: Language): Promise<Language> {
    if (!lang) {
      return this.sessionService.getReportLang();
    }
    return Promise.resolve(lang);
  }

}
